import { createContext, useContext, useState, ReactNode } from 'react';

type Settings = {
	activePlayer?: string;
	highlightCorners?: boolean;
	lastTurnSent?: string;
	theme?: string;
	highContrast?: boolean;
};

type SettingsContextType = {
	settings: Settings;
	setValue: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
};

const SettingsContext = createContext<SettingsContextType>({
	settings: {},
	setValue: () => {},
});

export function SettingsProvider({ children }: { children: ReactNode }) {
	const getSetting = <K extends keyof Settings>(key: K): Settings[K] => {
		const storedValue = localStorage.getItem(key);
		return storedValue === null ? undefined : JSON.parse(storedValue);
	};
	const [settings, setSettings] = useState<Settings>(() => ({
		activePlayer: getSetting('activePlayer'),
		highlightCorners: getSetting('highlightCorners'),
		lastTurnSent: getSetting('lastTurnSent'),
		theme: getSetting('theme'),
		highContrast: getSetting('highContrast'),
	}));

	const setValue = <K extends keyof Settings>(key: K, value: Settings[K]) => {
		if (value === undefined || value === null) {
			localStorage.removeItem(key);
		} else {
			localStorage.setItem(key, JSON.stringify(value));
		}
		setSettings({
			...settings,
			[key]: value,
		});
	};

	return (
		<SettingsContext.Provider value={{ settings, setValue }}>
			{children}
		</SettingsContext.Provider>
	);
}

export function useSettings() {
	return useContext(SettingsContext);
}
